import "./AllVenueList.scss"

import React, { useState, useCallback } from "react";
import { Typography, Collapse, Button, List, Space, Tabs } from "antd";
import { LeftOutlined, WarningOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { IAnalyticsAction, IApplicationState } from "../../store";
import ISite from "../../interfaces/ISite";
import LoadingSpinner from "../loading-spinner/loading-spinner";
import { useTranslation } from "react-i18next";
import useLocationsWithVenues, { CountryEntry, RegionEntry, StateEntry, VenueEntry } from "../../hooks/useLocationsWithVenues";
import useSiteChange from "../../hooks/useSiteChange";
import { SiteChangeSource } from "../../types/SiteChangeTypes";
import { GoogleTagManagerEvents } from "../../helpers/googleTagManager";

const { Title, Text } = Typography;

interface IAllVenueListProps {
    onSiteChange?: (site: ISite) => void;
    parentContainerId: string;
}

function safeLocaleCompare(
    a: string | null | undefined,
    b: string | null | undefined,
    locales?: string | string[],
    options?: Intl.CollatorOptions
): number {
    const sa = a ?? "";
    const sb = b ?? "";
    return sa.localeCompare(sb, locales, options);
}


const AllVenueList: React.FC<IAllVenueListProps> = ({ onSiteChange, parentContainerId }) => {
    const { t } = useTranslation();

    const { changeSite } = useSiteChange();
    const dispatch = useDispatch();

    const allSites = useSelector((state: IApplicationState) => state.sites.sites);
    const sitesLoading = useSelector((s: IApplicationState) => s.sites.sitesLoading);
    const sitesLoaded = useSelector((s: IApplicationState) => s.sites.sitesLoaded);

    const { nestedMap: nestedLocationsMap } = useLocationsWithVenues();

   // Utility function to scroll a collapse header to the top of the widget
    const scrollHeaderToTop = useCallback((panelKey: string | string[]) => {

        //console.log(panelKey);

        // Wait for collapse animation to complete
        setTimeout(() => {
            const key = Array.isArray(panelKey) ? panelKey[0] : panelKey;
            if (!key) return;

            const container = document.querySelector(`#${parentContainerId}`) as HTMLElement | null;
            const header = document.getElementById(`panel-header-${key}`);

            if (container && header) {
                const containerRect = container.getBoundingClientRect();
                const headerRect = header.getBoundingClientRect();
                const target = container.scrollTop + (headerRect.top - containerRect.top);

                container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
            }
            else {
                console.log(`couldn't find container:${container} or header:${header}`);
            }

        }, 200); // Wait for Ant Design collapse animation
    }, [parentContainerId]);

    // swap our azure regionIds to translated labels, or fall thru to displaying the seoRegion name when that data bcomes available
    const region1 = t(`locationWidget.regions.1`);
    const region2 = t(`locationWidget.regions.2`);
    const region3 = t(`locationWidget.regions.3`);

    const regionLabelMap: Record<string, string> = {
        '1': region1,
        '2': region2,
        '3': region3,
    };
    const getRegionHeader = (val?: string | number) => {
        const v = String(val ?? '').trim();
        return regionLabelMap[v] ?? v;
    };

    const backButtonLabel = t('locationWidget.actions.back');
    const allLocationsLabel = t('locationWidget.titles.allLocations');

    const regionsTabName = 'region-country';
    const venuesTabName = 'state-venue';

    const [activeTab, setActiveTab] = useState<string>(regionsTabName);
    const [selectedRegion, setSelectedRegion] = useState<RegionEntry | null>(null);
    const [selectedCountry, setSelectedCountry] = useState<CountryEntry | null>(null);
    //const [selectedState, setSelectedState] = useState<StateEntry | null>(null);


    const onRegionClicked = (panelKey: string | string[]) => {
        //console.log(panelKey);
        //grab the first regionKey
        const regionKey = Array.isArray(panelKey) ? panelKey[0] : panelKey;
        //remember the selected region
        const clickedRegion = nestedLocationsMap?.[regionKey];
        setSelectedRegion(clickedRegion);
        //ensure clicked header is scrolled into view
        scrollHeaderToTop(panelKey);

        dispatch({
            type: 'ANALYTICS_EVENT',
            googleTagManagerEvent: GoogleTagManagerEvents.LocationWidgetClick_region(clickedRegion?.regionData?.key ?? 'none', clickedRegion?.regionData?.value ?? 'none'),
        } as IAnalyticsAction);
    };

    const onCountryClicked = (country: CountryEntry) => {
        //remember the selected country
        setSelectedCountry(country);
        //bump over to country-venue tab
        setActiveTab(venuesTabName);

        dispatch({
            type: 'ANALYTICS_EVENT',
            googleTagManagerEvent: GoogleTagManagerEvents.LocationWidgetClick_country(country?.countryData?.key ?? 'none', country?.countryData?.value ?? 'none'),
        } as IAnalyticsAction);
    };

    const onBackClick = () => {
        setSelectedCountry(null)
        setActiveTab(regionsTabName);

        dispatch({
            type: 'ANALYTICS_EVENT',
            googleTagManagerEvent: GoogleTagManagerEvents.LocationWidget_backClicked(),
        } as IAnalyticsAction);
    };

    const onStateClicked = (panelKey: string | string[]) => {
        //grab the first state key
        const stateKey = Array.isArray(panelKey) ? panelKey[0] : panelKey;
        //check we can find the states data in our selected country
        if (!selectedCountry) {
            console.warn(`user clicked state with key '${stateKey}', but a country was not currently selected`);
            return;
        }
        //remember the selected state
        const clickedState = selectedCountry.states[stateKey];
        //setSelectedState(selectedCountry.states[stateKey]);

        //ensure clicked header is scrolled into view
        scrollHeaderToTop(panelKey);

        dispatch({
            type: 'ANALYTICS_EVENT',
            googleTagManagerEvent: GoogleTagManagerEvents.LocationWidgetClick_state(clickedState?.stateData?.key ?? 'none', clickedState?.stateData?.value ?? 'none'),
        } as IAnalyticsAction);
    };

    const onVenueClick = async (siteId: number) => {
        const clickedSite = allSites.find(x => x.siteId === siteId);
        if (clickedSite) {
            await changeSite(clickedSite, { source: SiteChangeSource.VenueWidget }, false, false, true);

            // Notify parent if callback provided
            if (onSiteChange) {
                onSiteChange(clickedSite);
            }
        }
    };


    // =================================================================================
    // Ui fragments that we return 

    if (sitesLoading) return (
        <div style={{ marginBottom: '20px' }} >
            <LoadingSpinner />
        </div>
    );

    if (!sitesLoading && (!sitesLoaded || !nestedLocationsMap)) {
        return (
            <WarningOutlined style={{ color: "red", fontSize: 32, width: "100%" }} />
        );
    }

    //define how each clickable venue should appear
    const renderVenueListItem = (venue: VenueEntry) => {
        return (
            <List.Item key={venue.id}>
                <Button
                    type="link"
                    block
                    className="venue-btn"
                    onClick={() => onVenueClick?.(venue.id)}
                >
                    <div className="venue-btn">
                        <Text strong>{venue.value?.toUpperCase()}</Text>
                        <div className="venue-details">
                            <div className="venue-address">
                                <Typography.Paragraph>
                                    {venue.address1 && <>{venue.address1}<br /></>}
                                    {venue.address2 && <>{venue.address2}<br /></>}
                                    {venue.address3 && <>{venue.address3}<br /></>}
                                </Typography.Paragraph>
                            </div>
                            <div className="venue-distance">
                                <Typography.Text>
                                    {venue.distanceInMeters !== undefined
                                        ? `${(venue.distanceInMeters / 1000)?.toFixed(1)} km`
                                        : '-'}
                                </Typography.Text>
                            </div>
                        </div>
                    </div>
                </Button>
            </List.Item>
        );
    };

    // define how each state/provence should be rendered
    const renderStateContent = (state: StateEntry) => {
        const serviceAreas = Object.values(state.serviceAreas).sort((a, b) =>
            safeLocaleCompare(a.areaData.value, b.areaData.value, undefined, { numeric: true, sensitivity: "base" })
        );
        const showAreaTitle = serviceAreas.length > 1;

        return (
            <div className="VenuesInStateList">
                {serviceAreas.map((area) => (
                    <div key={area.areaData.key}>
                        {showAreaTitle && (
                            <Title level={3} style={{ fontWeight: "bold" }}>
                                {area.areaData.value?.toUpperCase()}
                            </Title>
                        )}
                        <List
                            className={`venueItems ${showAreaTitle ? 'with-state-heading': ''}` }
                            dataSource={[...area.venues].sort((a, b) =>
                                safeLocaleCompare(a.value, b.value, undefined, { numeric: true, sensitivity: "base" })
                            )}
                            renderItem={(v) => renderVenueListItem(v)}
                        />
                    </div>
                ))}
            </div>
        );
    };

    //render the content for a selected country (eg list of available states and venues)
    const renderCountryContent = (country: CountryEntry) => {
        //if we havent selected a country, we can't build  list for its states and venues
        if (!country) return (<></>);

        //build a list of each state in the country
        const items =
            Object.values(country.states).sort((a, b) =>
                safeLocaleCompare(a.stateData.value, b.stateData.value, undefined, { numeric: true, sensitivity: "base" })
            )
            .map((state) => ({
                key: state.stateData.key,
                header: state.stateData.value?.toUpperCase(),
                content: renderStateContent(state),
            }));


        //if there are multiple states in the country use a collapse list, one section per state
        if (items.length > 1) {
            return (
                <Collapse accordion onChange={onStateClicked}>
                    {items.map((it) => (
                        <Collapse.Panel header={it.header} key={it.key} id={`panel-header-${it.key}`}>
                            {it.content}
                        </Collapse.Panel>
                    ))}
                </Collapse>
            );
        }
        else if (items.length === 1) {
            //if there is only one state just return that state view directly without nesting
            return (items[0].content);
        }


        //else
        return (<div>No venues in country</div>);
    };

    // Build state-venue content for the second tab
    const renderStateVenueTab = () => {
        if (!selectedCountry) return (<div>No country selected</div>);

        return (
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
                <Title level={2} style={{ marginBottom: 0 }}>
                    {t('locationWidget.titles.countryLocations', { country: selectedCountry.countryData.value?.toUpperCase() })}
                </Title>

                <Button
                    id="allVenueListBackBtn"
                    type="link"
                    icon={<LeftOutlined />}
                    onClick={onBackClick}
                >
                    {backButtonLabel}
                </Button>

                {renderCountryContent(selectedCountry)}
                
            </Space>
        );
    };

    //render the content for the world (eg all the regions and the country options in them)
    const renderWorldContent = () => {
        if (!nestedLocationsMap) return (<>No region data</>);

        //build a list of each region and how we want it titled and keyed
        const items = Object.values(nestedLocationsMap).sort((a, b) =>
            safeLocaleCompare(a.regionData.value, b.regionData.value, undefined, { numeric: true, sensitivity: "base" })
            )
            .map((region) => ({
                key: region.regionData.key,
                header: getRegionHeader(region.regionData.value)?.toUpperCase(),
                content: renderRegionContent(region),
            }));

        //if there are multiple states in the country use a collapse list, one section per state
        if (items.length > 0) {
            return (
                <Collapse accordion onChange={onRegionClicked}>
                    {items.map((it) => (
                        <Collapse.Panel header={it.header} key={it.key} id={`panel-header-${it.key}`} >
                            {it.content}
                        </Collapse.Panel>
                    ))}
                </Collapse>
            );
        }

        //else
        return (<div>No regions rendered</div>);
    };

    //render the list of country options that will exist in a region
    const renderCountryListItem = (country: CountryEntry) => {
        return (
            <List.Item key={country.countryData.key}>
                <Button
                    className="countryBtn"
                    type="link"
                    onClick={() => onCountryClicked(country)}
                >
                    {country.countryData.value?.toUpperCase()}
                </Button>
            </List.Item>
        );

    };

    //build the content of the region (eg list of countries
    const renderRegionContent = (region: RegionEntry) => {
        return (
            <List
                className="country-list"
                dataSource={Object.values(region.countries).sort((a, b) =>
                    safeLocaleCompare(
                        a.countryData.value,
                        b.countryData.value,
                        undefined,
                        { numeric: true, sensitivity: "base" }
                    )
                )}
                renderItem={(country) => renderCountryListItem(country)}
            />
        );
    };

    // Build region-country content for tab
    const renderRegionCountryTab = () => (
        //Collapse list containing all the region names
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Title level={2} style={{ marginBottom: 0 }}>
                {allLocationsLabel}
            </Title>

            {renderWorldContent()}
        </Space>
    );

    //Put the region-country list and the state-venue list into tabs, so we can smothly transition the view
    const tabItems = [
        {
            key: regionsTabName,
            label: regionsTabName,
            children: renderRegionCountryTab(),
        },
        ...(selectedRegion && selectedCountry ? [{
            key: venuesTabName,
            label: venuesTabName,
            children: renderStateVenueTab(),
        }] : []),
    ];

    // Main tabbed view
    return (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <div id="all-venue-list">
                <Tabs
                    activeKey={activeTab}
                    items={tabItems}
                    animated={{ inkBar: true, tabPane: true }}
                    tabBarStyle={{ display: 'none' }}
                />
            </div>
        </Space>
    );
};

export default AllVenueList;

